import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { ArrowLeft, Building2, MapPin, Phone, Users, Edit, Plus, Loader2, X, Mail, MessageSquare, ExternalLink, Check, ChevronsUpDown, Pencil, Save, Globe } from 'lucide-react';
import { formatPhone } from '../../utils/formatPhone';
import { 
  useCompanyData, 
  useLocationEditor, 
  useTechStackEditor, 
  useWebsiteEditor, 
  useLeadManager,
  formatShorthand
} from '../../hooks/useCompanyDetail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Constants
const COUNTRIES = ['USA', 'Canada', 'UK', 'India', 'Australia', 'Germany', 'France', 'Other'];
const ATS_OPTIONS = ['JobDiva', 'BullHorn', 'Salesforce', 'Greenhouse', 'Workday', 'iCIMS', 'Taleo'];
const AI_VOICE_OPTIONS = ['JobTalk', 'ConverzAI', 'HeyMilo', 'Glider', 'Paradox', 'HireVue'];
const COMPANY_TYPE_OPTIONS = [
  { value: 'potential', label: 'Potential' },
  { value: 'client', label: 'Client' },
  { value: 'pot-comp', label: 'Pot-Comp' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'others', label: 'Others' }
];

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-green-100 text-green-700' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-700' },
];

// Searchable Combobox Component
const SearchableCombobox = ({ value, onChange, options, placeholder, testId }) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setOpen(false);
  };

  const handleInputChange = (newValue) => {
    onChange(newValue);
  };

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes((value || '').toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            value={value || ''}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={placeholder}
            className="pr-8 bg-white"
            onClick={() => setOpen(true)}
            data-testid={testId}
          />
          <ChevronsUpDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput 
            placeholder={`Search ${placeholder}...`} 
            value={value || ''}
            onValueChange={handleInputChange}
          />
          <CommandList>
            <CommandEmpty>
              {value ? (
                <button
                  className="w-full px-2 py-1.5 text-sm text-left hover:bg-slate-100 rounded"
                  onClick={() => handleSelect(value)}
                >
                  Use &ldquo;{value}&rdquo;
                </button>
              ) : (
                'No options found.'
              )}
            </CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => handleSelect(option)}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === option ? 'opacity-100' : 'opacity-0'}`} />
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const getStatusBadge = (status) => {
  const s = STATUSES.find(x => x.value === status) || STATUSES[0];
  return <Badge className={s.color}>{s.label}</Badge>;
};

const getCommIcon = (type) => {
  switch(type) {
    case 'email': return <Mail className="w-4 h-4 text-indigo-500" />;
    case 'sms': return <MessageSquare className="w-4 h-4 text-green-500" />;
    case 'call': return <Phone className="w-4 h-4 text-orange-500" />;
    default: return null;
  }
};

const getCommBgColor = (type) => {
  switch(type) {
    case 'email': return 'bg-indigo-500';
    case 'sms': return 'bg-green-500';
    case 'call': return 'bg-orange-500';
    default: return 'bg-slate-500';
  }
};

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Use custom hooks for state management
  const {
    company,
    loading,
    selectedLocationId,
    loadData,
    handleLocationClick,
    getSelectedLocationName,
    filteredLeads,
    filteredCommunications
  } = useCompanyData(id, navigate);

  const locationEditor = useLocationEditor(company, id, loadData);
  const techStackEditor = useTechStackEditor(company, id, loadData);
  const websiteEditor = useWebsiteEditor(company, id, loadData);
  const leadManager = useLeadManager(id, company?.name, selectedLocationId, loadData);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/companies')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {company.name}
            </h1>
            {/* Website URL */}
            {websiteEditor.isEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  type="url"
                  value={websiteEditor.editedWebsite}
                  onChange={(e) => websiteEditor.setEditedWebsite(e.target.value)}
                  placeholder="https://example.com"
                  className="h-8 w-64 text-sm"
                  data-testid="edit-website-input"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-green-600 hover:text-green-700"
                  onClick={websiteEditor.save}
                  disabled={websiteEditor.saving}
                >
                  {websiteEditor.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-400 hover:text-slate-600"
                  onClick={websiteEditor.cancelEditing}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {company.website ? (
                  <a
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                    data-testid="company-website-link"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="underline">{company.website}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-sm text-slate-400 flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    No website
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-slate-400 hover:text-slate-600"
                  onClick={websiteEditor.startEditing}
                  data-testid="edit-website-btn"
                >
                  <Pencil className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 text-slate-500 text-sm mt-1">
            <span className="flex items-center gap-1">
              <Building2 className="w-4 h-4" />
              {company.locations?.length || 0} locations
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {filteredLeads.length} leads{getSelectedLocationName() && ` in ${getSelectedLocationName()}`}
            </span>
          </div>
        </div>
      </div>

      {/* Location filter notification */}
      {selectedLocationId && (
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2 flex items-center justify-between">
          <span className="text-indigo-700 text-sm">
            Showing leads and communications for <strong>{getSelectedLocationName()}</strong>
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleLocationClick(selectedLocationId)}
            className="text-indigo-600 hover:text-indigo-800"
          >
            Clear filter
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Locations */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-indigo-500" />
                Locations
              </CardTitle>
              <Button size="sm" variant="outline" onClick={locationEditor.openAddLocation} data-testid="add-location-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {company.locations?.length > 0 ? (
              company.locations.map((loc, idx) => (
                <div 
                  key={loc.id || idx}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedLocationId === loc.id 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-slate-200 hover:border-indigo-300'
                  }`}
                  onClick={() => handleLocationClick(loc.id)}
                  data-testid={`location-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{loc.name}</p>
                      {loc.address && <p className="text-sm text-slate-500">{loc.address}</p>}
                      {(loc.city || loc.state || loc.zip) && (
                        <p className="text-sm text-slate-500">
                          {[loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {loc.phone && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />
                          {formatPhone(loc.phone)}
                        </p>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 -mt-1 -mr-1"
                      onClick={(e) => locationEditor.openEditLocation(loc, idx, e)}
                    >
                      <Edit className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 py-4 text-center">No locations yet</p>
            )}
          </CardContent>
        </Card>

        {/* Tech Stack & Revenue Section */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Company Details</CardTitle>
              {!techStackEditor.isEditing ? (
                <Button size="sm" variant="outline" onClick={techStackEditor.startEditing} data-testid="edit-tech-stack-btn">
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={techStackEditor.cancelEditing}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={techStackEditor.save} disabled={techStackEditor.saving} data-testid="save-tech-stack-btn">
                    {techStackEditor.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {techStackEditor.isEditing ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Company Type</Label>
                  <Select value={techStackEditor.editedCompanyType} onValueChange={techStackEditor.setEditedCompanyType}>
                    <SelectTrigger className="mt-1" data-testid="edit-company-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANY_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">ATS Used</Label>
                  <SearchableCombobox
                    value={techStackEditor.editedAtsUsed}
                    onChange={techStackEditor.setEditedAtsUsed}
                    options={ATS_OPTIONS}
                    placeholder="ATS"
                    testId="edit-ats-used"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">AI Voice Tool</Label>
                  <SearchableCombobox
                    value={techStackEditor.editedAiVoiceUsing}
                    onChange={techStackEditor.setEditedAiVoiceUsing}
                    options={AI_VOICE_OPTIONS}
                    placeholder="AI Voice"
                    testId="edit-ai-voice"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Other AI Tools</Label>
                  <Input
                    value={techStackEditor.editedOtherAiTools}
                    onChange={(e) => techStackEditor.setEditedOtherAiTools(e.target.value)}
                    placeholder="Other tools..."
                    className="mt-1"
                    data-testid="edit-other-ai-tools"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Revenue (e.g., 50M, 1.5B)</Label>
                  <Input
                    value={techStackEditor.editedTotalRevenue}
                    onChange={(e) => techStackEditor.setEditedTotalRevenue(e.target.value)}
                    placeholder="50M, 1.5B..."
                    className="mt-1"
                    data-testid="edit-total-revenue"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Jobs (e.g., 500, 10K)</Label>
                  <Input
                    value={techStackEditor.editedTotalJobs}
                    onChange={(e) => techStackEditor.setEditedTotalJobs(e.target.value)}
                    placeholder="500, 10K..."
                    className="mt-1"
                    data-testid="edit-total-jobs"
                  />
                </div>
                <div className="col-span-2 md:col-span-3">
                  <Label className="text-xs text-slate-500">Company Notes</Label>
                  <Textarea
                    value={techStackEditor.editedCompanyNotes}
                    onChange={(e) => techStackEditor.setEditedCompanyNotes(e.target.value)}
                    placeholder="Internal notes about the company..."
                    className="mt-1"
                    rows={3}
                    data-testid="edit-company-notes"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {company.company_type && (
                  <Badge variant="outline" className="bg-slate-50">
                    Type: {COMPANY_TYPE_OPTIONS.find(o => o.value === company.company_type)?.label || company.company_type}
                  </Badge>
                )}
                {company.ats_used && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">ATS: {company.ats_used}</Badge>
                )}
                {company.ai_voice_using && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700">AI Voice: {company.ai_voice_using}</Badge>
                )}
                {company.other_ai_tools && (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Tools: {company.other_ai_tools}</Badge>
                )}
                {company.total_revenue && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Revenue: {formatShorthand(company.total_revenue)}</Badge>
                )}
                {company.total_jobs && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700">Jobs: {formatShorthand(company.total_jobs)}</Badge>
                )}
                {!company.company_type && !company.ats_used && !company.ai_voice_using && !company.other_ai_tools && !company.total_revenue && !company.total_jobs && !company.company_notes && (
                  <span className="text-sm text-slate-400">No details added yet</span>
                )}
              </div>
            )}
            {!techStackEditor.isEditing && company.company_notes && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-semibold text-amber-800 mb-1">Notes:</p>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{company.company_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Leads and Communications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                Leads
                {getSelectedLocationName() && (
                  <Badge variant="outline" className="ml-2 text-xs">{getSelectedLocationName()}</Badge>
                )}
              </CardTitle>
              <Button size="sm" variant="outline" onClick={leadManager.openAddLeadDialog} data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Lead
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredLeads.length > 0 ? (
                filteredLeads.map(lead => (
                  <div 
                    key={lead.id}
                    className="p-3 rounded-lg border border-slate-200 hover:border-indigo-300 cursor-pointer transition-all"
                    onClick={() => navigate(`/admin/leads/${lead.id}`)}
                    data-testid={`lead-${lead.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">
                          {lead.first_name} {lead.last_name || ''}
                        </p>
                        {lead.title && <p className="text-sm text-slate-500">{lead.title}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          {lead.work_email && (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {lead.work_email}
                            </span>
                          )}
                        </div>
                      </div>
                      {getStatusBadge(lead.status)}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-400 py-8 text-center">
                  {selectedLocationId ? 'No leads at this location' : 'No leads yet'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Communications */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-500" />
              Recent Communications
              {getSelectedLocationName() && (
                <Badge variant="outline" className="ml-2 text-xs">{getSelectedLocationName()}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredCommunications.length > 0 ? (
                filteredCommunications.slice(0, 10).map((comm, idx) => (
                  <div 
                    key={comm.id || idx}
                    className="flex gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all"
                    onClick={() => comm.lead_id && navigate(`/admin/leads/${comm.lead_id}`)}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getCommBgColor(comm.type)}`}>
                      {getCommIcon(comm.type) || <MessageSquare className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-slate-900 text-sm">{comm.lead_name}</p>
                        <span className="text-xs text-slate-400">
                          {comm.created_at && formatDistanceToNow(parseISO(comm.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{comm.content || comm.subject || 'No content'}</p>
                      <Badge variant="outline" className="mt-1 text-xs capitalize">{comm.type}</Badge>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-400 py-8 text-center">
                  {selectedLocationId ? 'No communications at this location' : 'No communications yet'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Location Dialog */}
      <Dialog open={locationEditor.editLocationOpen} onOpenChange={locationEditor.setEditLocationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {locationEditor.editingLocationIndex === -1 ? 'Add Location' : 'Edit Location'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Location Name *</Label>
              <Input
                value={locationEditor.editingLocation?.name || ''}
                onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, name: e.target.value})}
                placeholder="Headquarters, Branch Office, etc."
                data-testid="location-name-input"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={locationEditor.editingLocation?.address || ''}
                onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, address: e.target.value})}
                placeholder="Street address"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>City</Label>
                <Input
                  value={locationEditor.editingLocation?.city || ''}
                  onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, city: e.target.value})}
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={locationEditor.editingLocation?.state || ''}
                  onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, state: e.target.value})}
                />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input
                  value={locationEditor.editingLocation?.zip || ''}
                  onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, zip: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={locationEditor.editingLocation?.phone || ''}
                onChange={(e) => locationEditor.setEditingLocation({...locationEditor.editingLocation, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            {locationEditor.editingLocationIndex !== -1 && (
              <Button 
                variant="destructive" 
                onClick={locationEditor.handleDeleteLocation}
                disabled={locationEditor.saving}
              >
                Delete
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => locationEditor.setEditLocationOpen(false)}>
                Cancel
              </Button>
              <Button onClick={locationEditor.handleSaveLocation} disabled={locationEditor.saving} data-testid="save-location-btn">
                {locationEditor.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                {locationEditor.editingLocationIndex === -1 ? 'Add' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={leadManager.addLeadOpen} onOpenChange={leadManager.setAddLeadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={leadManager.addLeadForm.first_name}
                  onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, first_name: e.target.value})}
                  placeholder="John"
                  data-testid="lead-first-name"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={leadManager.addLeadForm.last_name}
                  onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, last_name: e.target.value})}
                  placeholder="Doe"
                  data-testid="lead-last-name"
                />
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={leadManager.addLeadForm.title}
                onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, title: e.target.value})}
                placeholder="VP of Sales"
              />
            </div>
            <div>
              <Label>Work Email</Label>
              <Input
                type="email"
                value={leadManager.addLeadForm.work_email}
                onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, work_email: e.target.value})}
                placeholder="john@company.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mobile Phone</Label>
                <Input
                  value={leadManager.addLeadForm.mobile_phone}
                  onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, mobile_phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label>Work Direct</Label>
                <Input
                  value={leadManager.addLeadForm.work_direct_phone}
                  onChange={(e) => leadManager.setAddLeadForm({...leadManager.addLeadForm, work_direct_phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select 
                  value={leadManager.addLeadForm.status} 
                  onValueChange={(val) => leadManager.setAddLeadForm({...leadManager.addLeadForm, status: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location</Label>
                <Select 
                  value={leadManager.addLeadForm.location_id} 
                  onValueChange={(val) => leadManager.setAddLeadForm({...leadManager.addLeadForm, location_id: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {company.locations?.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => leadManager.setAddLeadOpen(false)}>
              Cancel
            </Button>
            <Button onClick={leadManager.handleAddLead} disabled={leadManager.saving} data-testid="save-lead-btn">
              {leadManager.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Add Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
